import { useState } from 'react';
import { message } from 'antd';
import { exportToExcel, importFromExcel, generateTemplate, validateImportedData, ExcelColumn } from '../utils/excelUtils';

export interface UseExcelImportExportOptions<T> {
  filename: string;
  sheetName?: string;
  columns: ExcelColumn[];
  data: T[];
  onImport?: (importedData: T[]) => void;
  validationRules?: {
    [key: string]: {
      required?: boolean;
      type?: 'string' | 'number' | 'boolean';
      validator?: (value: any) => boolean;
      errorMessage?: string;
    };
  };
  transformImportedData?: (item: any) => T;
  skipValidation?: boolean; // Whether to skip validation during import (default: false)
}

export const useExcelImportExport = <T>(options: UseExcelImportExportOptions<T>) => {
  const [importLoading, setImportLoading] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);

  const handleExportExcel = () => {
    if (options.data.length === 0) {
      message.warning('No data to export');
      return;
    }

    exportToExcel({
      filename: `${options.filename}_${new Date().toISOString().split('T')[0]}`,
      sheetName: options.sheetName || 'Data',
      columns: options.columns,
      data: options.data
    });
    message.success('Data exported to Excel successfully');
  };

  const handleDownloadTemplate = () => {
    generateTemplate(
      `${options.filename}_Template_${new Date().toISOString().split('T')[0]}`,
      options.columns.filter(col => !col.title.toLowerCase().includes('calculated')) // Exclude calculated fields
    );
    message.success('Template downloaded successfully');
  };

  const handleImportExcel = async (file: File, columnMapping?: { [title: string]: string }) => {
    setImportLoading(true);

    console.log("=== useExcelImportExport handleImportExcel ===");
    console.log("Expected columns:", options.columns.map(col => col.title));
    console.log("Column mapping received:", columnMapping);

    try {
      const result = await importFromExcel<Partial<T>>(file, {
        expectedColumns: options.columns.map(col => col.title),
        headerRow: 0,
        preserveEmptyValues: true, // Preserve empty cells as null
        columnMapping: columnMapping // Pass the column mapping
      });

      if (result.errors.length > 0) {
        message.error(`Import failed: ${result.errors.join(', ')}`);
        setImportLoading(false);
        return;
      }

      if (result.warnings.length > 0) {
        message.warning(`Import warnings: ${result.warnings.join(', ')}`);
      }

      let processedData = result.data;

      // Apply validation if rules are provided and not explicitly skipped
      if (options.validationRules && !options.skipValidation) {
        const { validData, errors } = validateImportedData<Partial<T>>(result.data, options.validationRules);

        if (errors.length > 0) {
          const errorMessage = errors.slice(0, 5).map(e => `Row ${e.row}, ${e.field}: ${e.message}`).join('\n');
          const moreErrors = errors.length > 5 ? `\n... and ${errors.length - 5} more errors` : '';
          message.error(`Validation errors:\n${errorMessage}${moreErrors}`, 8); // Show for 8 seconds
          setImportLoading(false);
          return;
        }

        processedData = validData;
      }

      // Transform data if transformer is provided
      let finalData: T[];
      if (options.transformImportedData) {
        finalData = processedData.map(options.transformImportedData);
      } else {
        finalData = processedData as T[];
      }

      // Call the import handler
      if (options.onImport) {
        options.onImport(finalData);
      }

      message.success(`Successfully imported ${finalData.length} records`);

      // ============================================================
      // AUTO-SAVE DISABLED - Commented out for now
      // ============================================================
      // // IMPORTANT: Trigger immediate save after import to persist data
      // // This ensures imported data is saved to localStorage immediately
      // // rather than waiting for the 5-second autosave delay
      // if (typeof window !== 'undefined') {
      //   // Dispatch a custom event that can trigger immediate save
      //   window.dispatchEvent(new CustomEvent('excel-import-complete', {
      //     detail: { recordCount: finalData.length }
      //   }));
      // }

      // Close modal after successful import
      setImportModalVisible(false);
    } catch (error) {
      message.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setImportLoading(false);
    }
  };

  return {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleExportExcel,
    handleDownloadTemplate,
    handleImportExcel,
  };
};

export default useExcelImportExport;